<i18n>
[
    "product__recommendationTray--label",
]
</i18n>

<template>
    <Layout :flushWithHeader="true" :noBottomPadding="true">
        <Transition name="c-product__fade" mode="out-in">
            <div :key="view">
                <AsyncProductTour
                    v-if="shouldShowProductTour"
                    :tourName="productTourName"
                    :productTourData="productTourData"
                />
                <div class="o-row-full o-row-full--no-gap c-product">
                    <div class="o-extra-small--12 o-large--7">
                        <ImageViewer
                            :productImages="imageUrls"
                            :choiceId="selectedChoice.choiceId"
                            :productName="productDetails.displayName"
                            :imagesFullWidth="true"
                            :analyticsType="pdpCarouselAnalyticsType"
                            :isBuyMode="isBuyMode"
                        />
                    </div>
                    <div class="o-extra-small--12 o-large--5 c-product__sku-container">
                        <ProductInfo
                            :productDetails="productDetails"
                            :prePurchaseEligibleChoices="prePurchaseEligibleChoices"
                            :colorCode="colorCode"
                            :selectedChoice="selectedChoice"
                            :isBuyMode="isBuyMode"
                            :productAnalyticsData="productAnalyticsData"
                            :isLoggedIn="isLoggedIn"
                        />
                    </div>
                </div>
                <div class="o-row-full c-product__rec-tray-wrapper">
                    <div class="o-extra-small--12">
                        <LazyLoad
                            v-slot="{ hasEnteredViewport }"
                        >
                            <YMALTray
                                :id="PDP_PRODUCT_TRAY_ID"
                                :listId="productDetails.slug"
                                :listName="productTrayListName"
                                :choiceId="selectedChoice.choiceId"
                                :applySizePreferencesAndAvailability="true"
                                :categoryQuery="ymalTrayCategoryQuery"
                                :shouldShowHeaderLink="false"
                                :lightInfoContainer="false"
                                :analyticsType="recommendationCarouselAnalyticsType"
                                :productsRelatedToColorData="productsRelatedToColorData"
                                :hasProductsRelatedToColorData="hasProductsRelatedToColorData"
                                :traceId="traceId"
                                :showProductInfo="true"
                                :isBuyMode="isBuyMode"
                                :hasEnteredViewport="hasEnteredViewport"
                                class="c-product__rec-tray"
                            />
                        </LazyLoad>
                    </div>
                </div>
                <div
                    v-if="shouldDisplayReviews"
                    class="c-product__reviews-summary-container"
                >
                    <ProductReviewsSummary v-if="!productHasNoReviews" />
                </div>
            </div>
        </Transition>
    </Layout>
</template>

<!-- Separate script tag needed for custom fetchData and vuex logic, to be used in entry-server and entry-client -->
<script>
import { getProductModuleName, getProductModuleNameFromRoute } from '~coreRoutes/product/js/product-utils';
import { SET_404_ERROR, GLOBAL_EVENT, UNIVERSAL_REDIRECT } from '~coreModules/core/js/store';
import { getNamespacedMethods } from '~coreModules/core/js/store-utils';

import { getPdpRedirect, getPdpQueryParams } from '~routes/product/js/product-utils';
import productModule, {
    FETCH_PRODUCT_DETAILS,
    FETCH_PRODUCT_REVIEWS_IMAGE_ONLY,
    SET_COLOR_CODE,
    SET_VIEW_MODE,
    RESET_PRODUCT_REVIEWS_IMAGES_ONLY,
    RESET_PRODUCT_REVIEW_SUMMARY,
} from '~routes/product/js/product-store';
import {
    PDP_PRODUCT_TRAY_ID,
    PRODUCT_PAGE_VIEW_MODES,
} from '~routes/product/js/product-constants';

// Process updates if route.path changes, but nothing else
// eslint-disable-next-line no-unused-vars
const shouldProcessRouteUpdate = {
    path: true,
    query: false,
    hash: false,
};

const vuex = {
    moduleName: getProductModuleName,
    module: productModule,
};

async function fetchData({ store, route, router }) {
    const { getters, commit, dispatch } = store;
    const { loggedIn, productAttribution } = getters;
    const { params, query } = route;
    const { slug } = params;
    const { color, sku = null, view = null } = query;
    const moduleName = getProductModuleNameFromRoute(route);
    const { commit: moduleCommit, dispatch: moduleDispatch } = getNamespacedMethods(store, moduleName);
    const isBuyMode = view === PRODUCT_PAGE_VIEW_MODES.BUY;
    let response;

    // redirect to rent mode if currently in buy mode and user is not logged in
    if (isBuyMode && !loggedIn) {
        await dispatch(UNIVERSAL_REDIRECT, getPdpRedirect({
            slug,
            query: { ...query, view: null },
            router,
        }));
        return;
    }

    try {
        const { catalogResponseId, curationName } = productAttribution(slug);

        response = await moduleDispatch(FETCH_PRODUCT_DETAILS, {
            slug,
            color,
            view,
            catalogResponseId,
            curationName,
        });

        await moduleCommit(RESET_PRODUCT_REVIEWS_IMAGES_ONLY);
    } catch (error) {
        commit(SET_404_ERROR, { message: error?.data?.message });
        return;
    }

    const { choices } = response ?? [];
    if (!choices?.length) {
        commit(SET_404_ERROR, { message: `No choices found for product ${response?.slug}` });
        return;
    }

    const newQuery = getPdpQueryParams({ choices, color, sku, view });
    const { color: newColor, sku: newSku, view: newView } = newQuery;

    // redirect if sku or view changes, or if color is different AND it was provided in the query
    // if only color changed but wasnt provided in the query, we dont redirect so the product
    // is reachable without query params for seo reasons
    if ((color && color !== newColor) || sku !== newSku || view !== newView) {
        await dispatch(UNIVERSAL_REDIRECT, getPdpRedirect({
            slug,
            query: newQuery,
            router,
        }));
        return;
    }

    moduleCommit(SET_COLOR_CODE, newColor);

    try {
        await moduleDispatch(FETCH_PRODUCT_REVIEWS_IMAGE_ONLY);
    } catch (error) {
        await moduleCommit(RESET_PRODUCT_REVIEW_SUMMARY);
    }
}

export default {
    shouldProcessRouteUpdate,
    fetchData,
    vuex,
};
</script>

<script setup>
/* eslint-disable import/first */
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useState, useGetters, useMutations, useActions } from '~coreModules/core/js/composables/vuexHelpers';

import { VIEW_PRODUCT } from '~coreModules/core/js/global-event-constants';

import useProductJsonLd from '~routes/product/js/composables/use-product-json-ld.js';
import useProductAnalytics from '~routes/product/js/composables/use-product-analytics';

import { PRODUCT_TOUR_MODULE_NAME } from '~modules/product-tour/js/product-tour-store';
import { CATALOG_QUERY_NUULY_PROGRAMS } from '~modules/catalog/js/catalog-constants';
import { PRODUCT_LIST_NAMES, CAROUSEL_IDS } from '~modules/analytics/js/analytics-constants';
import { CLOSET_PRODUCT_TOUR_DATA } from '~modules/product-tour/js/product-tour-constants';
import productMetaMixin from '~routes/product/js/product-meta';

import ImageViewer from '~coreRoutes/product/components/ImageViewer.vue';

import Layout from '~modules/core/components/layouts/Layout.vue';
import ProductInfo from '~routes/product/components/ProductInfo.vue';
import ProductReviewsSummary from '~routes/product/components/ProductReviewsSummary.vue';
import YMALTray from '~modules/core/components/ui/YMALTray.vue';
import LazyLoad from '~coreModules/core/components/ui/LazyLoad.vue';
/* eslint-enable import/first */

// NU_TODO: https://urbnit.atlassian.net/browse/TYP-26682
// Remove below when we update to vue-eslint-parser v9.0.0 or later
/* eslint-disable no-undef */
defineOptions({
    mixins: [productMetaMixin],
});

const route = useRoute();
const productModuleName = computed(() => getProductModuleNameFromRoute(route));

useProductAnalytics();

const { isLoggedIn } = useGetters({ isLoggedIn: 'loggedIn' });

const {
    productDetails,
    colorCode,
    traceId,
} = useState(productModuleName.value, {
    productDetails: state => state.productDetails,
    colorCode: state => state.colorCode,
    traceId: state => state.traceId,
});

const {
    selectedChoice,
    productHasNoReviews,
    shouldShowWriteReviewLink,
    productsRelatedToColorData,
    hasProductsRelatedToColorData,
    productAnalyticsData,
    isBuyMode,
    prePurchaseEligibleChoices,
} = useGetters(productModuleName.value, [
    'selectedChoice',
    'productHasNoReviews',
    'shouldShowWriteReviewLink',
    'productsRelatedToColorData',
    'hasProductsRelatedToColorData',
    'productAnalyticsData',
    'isBuyMode',
    'prePurchaseEligibleChoices',
]);

const { updateViewMode, setColorCode } = useMutations(productModuleName.value, {
    updateViewMode: SET_VIEW_MODE,
    setColorCode: SET_COLOR_CODE,
});

const { shouldRenderGivenTour } = useGetters(PRODUCT_TOUR_MODULE_NAME, ['shouldRenderGivenTour']);

const { trackGlobalEvent } = useActions({ trackGlobalEvent: GLOBAL_EVENT });

const AsyncProductTour = defineAsyncComponent(() => import(
    /* webpackChunkName: "ProductTour" */
    '~modules/product-tour/components/ProductTour.vue'));

const productTrayListName = PRODUCT_LIST_NAMES.PDP_TRAY;
const recommendationCarouselAnalyticsType = CAROUSEL_IDS.YOU_MAY_ALSO_LIKE;
const pdpCarouselAnalyticsType = CAROUSEL_IDS.PDP_IMAGE_VIEWER;
const productTourData = CLOSET_PRODUCT_TOUR_DATA;

const view = ref('');

const imageUrls = computed(() => selectedChoice?.value?.imageUrls || []);
const productTourName = computed(() => Object.keys(productTourData).find(tour => tour));
const shouldShowProductTour = computed(() => shouldRenderGivenTour(productTourName.value));
const shouldDisplayReviews = computed(() => !(productHasNoReviews.value && !shouldShowWriteReviewLink.value));
const ymalTrayCategoryQuery = computed(() => (isBuyMode.value ?
    { [CATALOG_QUERY_NUULY_PROGRAMS]: 'prepurchase' } : {}));

useProductJsonLd(productDetails.value, imageUrls.value);

function scrollToTop() {
    if (process.env.VUE_ENV === 'client') requestAnimationFrame(() => { window.scrollTo(0, 0); });
}

watch(() => route.query, async (newQuery, oldQuery) => {
    if (newQuery?.color !== oldQuery?.color) {
        trackGlobalEvent({
            type: VIEW_PRODUCT,
            data: productAnalyticsData.value,
        });

        if (newQuery?.color !== selectedChoice?.value.color?.code) {
            setColorCode(newQuery?.color);
        }
    }

    if (newQuery?.view !== oldQuery?.view) {
        updateViewMode(newQuery?.view ?? PRODUCT_PAGE_VIEW_MODES.RENT);
        view.value = newQuery?.view;

        await new Promise(resolve => setTimeout(resolve, 500));

        scrollToTop();
    }
});

</script>

<style lang="scss">
    .c-product {
        margin-bottom: $nu-spacer-2;

        @include breakpoint(medium) {
            margin-bottom: 0;
        }

        &__sku-container {
            position: relative;
        }

        &__reviews-summary-container {
            background-color: $nu-secondary;
            padding: $nu-spacer-6 0;
            width: 100%;

            @include breakpoint(medium) {
                padding: $nu-spacer-8 0;
            }
        }

        &__rec-tray-wrapper {
            background: $nu-white;
        }

        &__rec-tray {
            padding: $nu-spacer-6 0 $nu-spacer-4;

            @include breakpoint(medium) {
                padding: $nu-spacer-8 0 $nu-spacer-6;
            }
        }

        &__fade-enter-active, &__fade-leave-active {
            transition: opacity 0.5s ease;
        }

        &__fade-enter-active {
            transition-delay: 0.1s; /* Delay for the fade-in effect */
        }

        &__fade-enter-from, &__fade-leave-to {
            opacity: 0;
        }

        &__fade-leave-active {
            transition-delay: 0s; /* No delay for the fade-out effect */
        }
    }
</style>
